import type { PortalAutoQuoteNode, PortalAutoQuoteOptionNode } from '@app/services/api/api.types';
import type {
  GoogleAirport,
  GoogleFlight,
  GoogleFlightOption,
  GoogleFlightSegment,
} from '@app/services/google-flights/google-flights.types';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function num(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function str(v: unknown, fallback = ''): string {
  if (v == null) {
    return fallback;
  }
  return String(v);
}

function middayTimestamp(isoDayOrDate: string | undefined | null): string {
  if (!isoDayOrDate) {
    return '2099-01-01T12:00:00';
  }
  const day = String(isoDayOrDate).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? `${day}T12:00:00` : String(isoDayOrDate);
}

function normalizeAirport(raw: unknown): GoogleAirport {
  if (!isRecord(raw)) {
    return { code: '—', name: '—', time: middayTimestamp(null) };
  }
  const code = str(raw.code ?? raw.locationCode, '—').trim() || '—';
  const name = str(raw.name ?? raw.locationName ?? raw.cityName ?? code, code).trim() || code;
  const timeRaw = raw.time ?? raw.departureAt ?? raw.arrivalAt;
  const time = str(timeRaw, '').trim() ? str(timeRaw) : middayTimestamp(null);
  return { code, name, time };
}

function normalizeFlightLeg(raw: unknown): GoogleFlight | null {
  if (!isRecord(raw)) {
    return null;
  }
  const dep = normalizeAirport(raw.departure_airport ?? raw.departureAirport);
  const arr = normalizeAirport(raw.arrival_airport ?? raw.arrivalAirport);
  const hasRoute = (dep.code && dep.code !== '—') || (arr.code && arr.code !== '—');
  if (!hasRoute) {
    return null;
  }

  const stops = num(raw.stops, 0);
  const durationVal = num(raw.duration);
  const segments: GoogleFlightSegment[] = [
    {
      airline: '',
      airline_logo: '',
      flight_number: '',
      departure_airport: dep,
      arrival_airport: arr,
      duration: durationVal,
      cabin_class: '',
      overnight: false,
    },
  ];

  return {
    departure_airport: dep,
    arrival_airport: arr,
    duration: durationVal,
    stops,
    stop_info: str(raw.stop_info ?? raw.stopInfo),
    is_nonstop: stops === 0,
    segments,
  };
}

function stringArray(v: unknown): string[] {
  if (!Array.isArray(v)) {
    return [];
  }
  return v.map((x) => str(x));
}

function normalizeApiOptionToGoogleFlightOption(
  opt: PortalAutoQuoteOptionNode,
): GoogleFlightOption | null {
  const o = opt as unknown as Record<string, unknown>;
  const outbound = normalizeFlightLeg(o.outbound);
  if (!outbound) {
    return null;
  }

  const inboundRaw = o.inbound;
  const inbound = inboundRaw == null || inboundRaw === '' ? null : normalizeFlightLeg(inboundRaw);

  const display = num(o.display_price ?? o.displayPrice);
  const priceVal = num(o.price, display);
  const travelClass = str(o.travel_class ?? o.travelClass, '').trim();

  return {
    price: priceVal || display,
    display_price: display || priceVal,
    markdown_percentage: 0,
    price_currency: 'USD',
    travel_class: travelClass || undefined,
    is_round_trip: Boolean(o.is_round_trip ?? o.isRoundTrip ?? inbound != null),
    outbound,
    inbound,
    emissions_kg: 0,
    emissions_info: '',
    airlines: stringArray(o.airlines),
    airline_logos: stringArray(o.airline_logos ?? o.airlineLogos),
  };
}

function airport(code: string | undefined, time: string): GoogleAirport {
  const c = (code ?? '—').trim() || '—';
  return { code: c, name: c, time };
}

function buildFlight(
  depCode: string | undefined,
  arrCode: string | undefined,
  depDate: string | undefined | null,
  arrDate: string | undefined | null,
): GoogleFlight {
  const dep = airport(depCode, middayTimestamp(depDate));
  const arr = airport(arrCode, middayTimestamp(arrDate ?? depDate));
  const segment: GoogleFlightSegment = {
    airline: '',
    airline_logo: '',
    flight_number: '',
    departure_airport: dep,
    arrival_airport: arr,
    duration: 0,
    cabin_class: '',
    overnight: false,
  };
  return {
    departure_airport: dep,
    arrival_airport: arr,
    duration: 0,
    stops: 0,
    stop_info: '',
    is_nonstop: true,
    segments: [segment],
  };
}

function syntheticOption(
  quote: PortalAutoQuoteNode,
  opt: PortalAutoQuoteOptionNode,
): GoogleFlightOption {
  const dep = quote.departureAirportCode;
  const arr = quote.arrivalAirportCode;
  const round = quote.isRoundTrip === true;
  const o = opt as unknown as Record<string, unknown>;

  const display = num(o.display_price ?? o.displayPrice ?? o.price, 0);
  const priceVal = num(o.price, display);
  const travelClass = str(o.travel_class ?? o.travelClass, '').trim();

  const outbound = buildFlight(dep, arr, null, null);
  const inbound = round && dep && arr ? buildFlight(arr, dep, null, null) : null;

  return {
    price: priceVal || display,
    display_price: display || priceVal,
    markdown_percentage: 0,
    price_currency: 'USD',
    travel_class: travelClass || undefined,
    is_round_trip: inbound != null,
    outbound,
    inbound,
    emissions_kg: 0,
    emissions_info: '',
    airlines: [],
    airline_logos: stringArray(o.airline_logos ?? o.airlineLogos),
  };
}

export function portalAutoQuoteOptionToGoogleFlightOption(
  quote: PortalAutoQuoteNode,
  opt: PortalAutoQuoteOptionNode,
): GoogleFlightOption {
  return normalizeApiOptionToGoogleFlightOption(opt) ?? syntheticOption(quote, opt);
}

export function portalAutoQuoteOptionsToGoogleFlightOptions(
  quote: PortalAutoQuoteNode,
): GoogleFlightOption[] {
  return quote.options.map((o) => portalAutoQuoteOptionToGoogleFlightOption(quote, o));
}
